import React, { useState, useEffect, useRef } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import { getTables, validateQuery } from '../services/api';
import { substituteVars } from '../utils/sqlVariables';

// SQL/ClickHouse keywords for intellisense
const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'LIMIT', 'AS',
  'AND', 'OR', 'IN', 'NOT', 'BETWEEN', 'LIKE', 'ILIKE', 'IS', 'NULL',
  'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'ON', 'USING',
  'WITH', 'HAVING', 'UNION', 'ALL', 'DISTINCT', 'EXISTS',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'ASC', 'DESC', 'BY',
  'count', 'sum', 'avg', 'min', 'max', 'uniqExact',
  'quantile', 'stddevSamp', 'varSamp',
  'toDateTime', 'toStartOfDay', 'toStartOfWeek', 'toStartOfMonth',
  'toStartOfInterval', 'formatDateTime', 'now', 'today',
  'JSONExtractString', 'JSONExtractInt', 'JSONExtractFloat',
  'hasToken', 'startsWith', 'endsWith',
  'avgIf', 'sumIf', 'countIf',
  'splitByChar', 'arrayJoin', 'groupArray',
  'IF', 'multiIf', 'INTERVAL',
  'SHOW', 'DESCRIBE', 'EXPLAIN',
];

// Git metrics specific suggestions
const GIT_METRICS = [
  'branch.commit', 'branch.commit.coauthor',
  'branch.time_to_pr', 'branch.time_to_review', 'branch.time_to_approve',
  'branch.time_to_merge', 'branch.review_time', 'branch.computed.cycle_time',
  'pr.opened', 'pr.merged', 'pr.review', 'pr.review.per_merged_pr',
  'pr.reviewer.time_to_review', 'pr.reviewer.time_to_approve',
  'commit.code_churn.refactor', 'commit.code_churn.rework',
  'pr.check_run'
];

const COLUMN_DOCS = {
  time: { type: 'DateTime64(3)', desc: 'Event timestamp' },
  metric: { type: 'String', desc: 'Metric name (e.g. branch.commit, pr.opened, pm.issue.state_change)' },
  value: { type: 'Float64', desc: 'Metric numeric value (duration in seconds for time metrics, count for others)' },
  meta: { type: 'String', desc: 'JSON metadata blob with extra event details' },
  source: { type: 'String', desc: "Data source: 'git' or 'pm'" },
  repository_id: { type: 'Int64', desc: 'Git repository identifier' },
  contributor_id: { type: 'Int64', desc: 'Contributor (developer) identifier' },
  contributor_name: { type: 'String', desc: 'Contributor display name' },
  contributor_is_human: { type: 'UInt8', desc: '1 if human contributor, 0 if bot' },
  team_id: { type: 'Int64', desc: 'Team identifier (from metric event)' },
  branch_id: { type: 'Int64', desc: 'Git branch identifier' },
  pull_request_id: { type: 'Int64', desc: 'Pull request identifier' },
  commit_id: { type: 'String', desc: 'Git commit identifier' },
  branch_release_id: { type: 'Int64', desc: 'Release identifier' },
  pr_url: { type: 'String', desc: 'Pull request HTML URL (clickable link)' },
  pr_title: { type: 'String', desc: 'Pull request title' },
  pr_state: { type: 'String', desc: 'PR state (open, merged, closed)' },
  pr_size_total: { type: 'Int64', desc: 'Total lines changed in PR (additions + deletions)' },
  pr_net_size_total: { type: 'Int64', desc: 'Net lines changed (additions - deletions)' },
  pr_team_id: { type: 'Int64', desc: 'Team ID of the PR issuer' },
  pr_team_name: { type: 'String', desc: 'Team name of the PR issuer (use for team filtering)' },
  branch_state: { type: 'String', desc: 'Branch state (active, merged, etc.)' },
  pr_labels: { type: 'Array(String)', desc: 'PR labels' },
  pr_author_type: { type: 'String', desc: 'Author type classification' },
  pr_maturity_ratio: { type: 'Float64', desc: 'Code maturity ratio' },
  pr_post_changes: { type: 'Int64', desc: 'Number of post-merge changes' },
  pr_unreviewed: { type: 'UInt8', desc: '1 if PR was merged without review' },
  pr_comment_count: { type: 'Int64', desc: 'Number of review comments on PR' },
  pr_check_run_status: { type: 'String', desc: 'CI check conclusion: success, failure, skipped, cancelled, neutral' },
  pr_check_type: { type: 'String', desc: 'CI check category: security, test, build, lint, automation, review, other' },
  pr_check_run_name: { type: 'String', desc: 'Raw CI check name (e.g. Orca Security - SAST)' },
  pr_check_html_url: { type: 'String', desc: 'CI check run URL (e.g. GitHub Actions job link)' },
  branch_is_included: { type: 'UInt8', desc: '1 if included in calculations' },
  branch_cycle_time_excluded: { type: 'UInt8', desc: '1 if excluded from cycle time' },
  pr_is_draft: { type: 'UInt8', desc: '1 if PR is a draft' },
  pr_is_merge: { type: 'UInt8', desc: '1 if commit is a merge commit' },
  branch_is_effective: { type: 'UInt8', desc: '1 if the event is effective' },
  pr_is_self_review: { type: 'UInt8', desc: '1 if PR was self-reviewed' },
  event_source: { type: 'String', desc: 'Source system of the event' },
  commit_sha: { type: 'String', desc: 'Full git commit SHA' },
  repo_url: { type: 'String', desc: 'Repository URL' },
  event_uuid: { type: 'String', desc: 'Unique event identifier' },
  branch_service_ids: { type: 'Array(Int64)', desc: 'Associated service identifiers' },
  // PM (issue) columns
  issue_id: { type: 'Int64', desc: 'Issue/ticket identifier' },
  related_issue_id: { type: 'Int64', desc: 'Related issue ID (links git events to PM issues)' },
  issue_type: { type: 'String', desc: 'Issue type classification' },
  provider_type: { type: 'String', desc: "Issue provider type (Story, Bug, Task, Epic, Sub-task)" },
  provider_key: { type: 'String', desc: 'Issue key in provider (e.g. PROJ-123)' },
  project_name: { type: 'String', desc: 'Project/board name in issue tracker' },
  issue_created_at: { type: 'DateTime64(3)', desc: 'When the issue was created' },
  issue_started_at: { type: 'DateTime64(3)', desc: 'When work started on the issue' },
  issue_completed_at: { type: 'DateTime64(3)', desc: 'When the issue was completed/resolved' },
  issue_priority: { type: 'String', desc: 'Issue priority (Critical, High, Medium, Low)' },
  issue_team: { type: 'String', desc: 'Team assigned to the issue' },
  issue_story_points: { type: 'Float64', desc: 'Story point estimate' },
  issue_html_url: { type: 'String', desc: 'Issue HTML URL (clickable link)' },
  issue_title: { type: 'String', desc: 'Issue title/summary' },
  issue_event_from_value: { type: 'String', desc: 'Previous state value (for state change events)' },
  issue_event_to_value: { type: 'String', desc: 'New state value (for state change events)' },
  // AI/Dev tools
  dev_tool_footprint_types: { type: 'Array(String)', desc: 'Types of AI/dev tool footprints detected' },
  dev_tool_names: { type: 'Array(String)', desc: 'Names of AI/dev tools used (e.g. Copilot, Cursor)' },
};

// Store tables for completion provider
let availableTables = ['metrics'];

// Configure Monaco before it loads
loader.init().then(monaco => {
  // Define a custom light theme
  monaco.editor.defineTheme('sql-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '0000FF' },
      { token: 'string', foreground: 'A31515' },
      { token: 'number', foreground: '098658' },
      { token: 'comment', foreground: '008000' },
      { token: 'delimiter', foreground: '000000' },
      { token: 'identifier', foreground: '001080' },
    ],
    colors: {
      'editor.foreground': '#333333',
      'editor.background': '#ffffff',
      'editorSuggestWidget.background': '#ffffff',
      'editorSuggestWidget.border': '#cccccc',
      'editorSuggestWidget.foreground': '#333333',
      'editorSuggestWidget.selectedBackground': '#e3f2fd',
      'editorSuggestWidget.selectedForeground': '#333333',
      'editorSuggestWidget.highlightForeground': '#1976d2',
      'editorSuggestWidget.focusHighlightForeground': '#1976d2',
      'list.hoverBackground': '#f5f5f5',
      'list.hoverForeground': '#333333',
      'list.focusBackground': '#e3f2fd',
      'list.focusForeground': '#333333',
      'list.activeSelectionBackground': '#1976d2',
      'list.activeSelectionForeground': '#ffffff',
      'list.inactiveSelectionBackground': '#e0e0e0',
      'list.inactiveSelectionForeground': '#333333',
    }
  });

  // Register completion provider for SQL
  monaco.languages.registerCompletionItemProvider('sql', {
    triggerCharacters: [' ', '.', '(', '\n', "'"],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // Get the text before cursor to determine context
      const lineContent = model.getLineContent(position.lineNumber);
      const textBeforeCursor = lineContent.substring(0, position.column - 1);

      // Check if we're inside quotes (for metric values)
      const singleQuotes = (textBeforeCursor.match(/'/g) || []).length % 2 === 1;

      const suggestions = [];

      // If inside quotes, suggest metric values
      if (singleQuotes) {
        GIT_METRICS.forEach(metric => {
          suggestions.push({
            label: metric,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: metric,
            range: range,
            detail: 'Metric value',
            sortText: '0' + metric
          });
        });
      } else {
        // Determine context for priority
        const upperText = textBeforeCursor.toUpperCase().trim();
        const afterFrom = /FROM\s*$/i.test(upperText);
        const afterSelect = /SELECT\s*$/i.test(upperText) || /,\s*$/i.test(upperText);

        // Tables (higher priority after FROM)
        availableTables.forEach(table => {
          suggestions.push({
            label: table,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: table,
            range: range,
            detail: 'Table',
            documentation: 'ClickHouse Table',
            sortText: afterFrom ? '0' + table : '3' + table
          });
        });

        // Keywords
        SQL_KEYWORDS.forEach(kw => {
          suggestions.push({
            label: kw,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: kw,
            range: range,
            detail: 'SQL Keyword',
            sortText: '2' + kw
          });
        });

        // Columns (higher priority after SELECT or WHERE)
        Object.entries(COLUMN_DOCS).forEach(([col, info]) => {
          suggestions.push({
            label: col,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: col,
            range: range,
            detail: `${info.type} — ${info.desc}`,
            sortText: afterSelect ? '0' + col : '1' + col
          });
        });

        // Template variables
        ['{start_date}', '{end_date}', '{team_name}'].forEach(v => {
          suggestions.push({
            label: v,
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: v,
            range: range,
            detail: 'Template variable (replaced at runtime)',
            sortText: '0' + v
          });
        });

        // Metric values with quotes
        GIT_METRICS.forEach(metric => {
          suggestions.push({
            label: `'${metric}'`,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: `'${metric}'`,
            range: range,
            detail: 'Metric value',
            sortText: '4' + metric
          });
        });
      }

      return { suggestions };
    }
  });
});

function SQLEditor({ onExecute, loading, query, setQuery, templateVars }) {
  const [validationStatus, setValidationStatus] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    // Fetch available tables for autocomplete
    getTables().then(tables => {
      if (tables.length > 0) {
        availableTables = tables;
      }
    }).catch(console.error);
  }, []);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Add keyboard shortcut for execute (Ctrl/Cmd + Enter)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      handleExecute();
    });
  };

  const handleValidate = async () => {
    try {
      const result = await validateQuery(query);
      setValidationStatus(result);
    } catch (err) {
      setValidationStatus({ is_valid: false, issues: [{ message: err.message }] });
    }
  };

  const handleExecute = () => {
    if (onExecute && !loading) {
      onExecute(substituteVars(query, templateVars));
    }
  };

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>SQL Query Editor</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleValidate}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid #1976d2',
              color: '#1976d2',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Validate
          </button>
          <button
            className="execute-btn"
            onClick={handleExecute}
            disabled={loading}
          >
            {loading ? 'Running...' : 'Run Query (Ctrl+Enter)'}
          </button>
        </div>
      </div>

      {validationStatus && (
        <div style={{
          padding: '0.5rem',
          marginBottom: '0.5rem',
          borderRadius: '4px',
          background: validationStatus.is_valid ? '#e8f5e9' : '#ffebee',
          color: validationStatus.is_valid ? '#2e7d32' : '#c62828',
          fontSize: '0.85rem'
        }}>
          {validationStatus.is_valid ? (
            'Query is valid'
          ) : (
            validationStatus.issues?.map((issue, i) => (
              <div key={i}>{issue.message}</div>
            ))
          )}
        </div>
      )}

      <div className="monaco-wrapper">
        <Editor
          height="300px"
          language="sql"
          theme="vs"
          value={query}
          onChange={setQuery}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            suggestOnTriggerCharacters: true,
            quickSuggestions: {
              other: true,
              comments: false,
              strings: true
            },
            parameterHints: { enabled: true },
            suggest: {
              showKeywords: true,
              showSnippets: true,
              showClasses: true,
              showFields: true,
              showValues: true,
              showMethods: true,
              showFunctions: true,
              filterGraceful: true,
              snippetsPreventQuickSuggestions: false,
              localityBonus: true,
              shareSuggestSelections: true,
              showStatusBar: true,
            },
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
            wordBasedSuggestions: 'currentDocument',
            fixedOverflowWidgets: true,
          }}
        />
      </div>

      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
        Tip: Press Ctrl+Space to trigger autocomplete for tables, columns, metrics, and SQL keywords
      </div>
    </div>
  );
}

export default SQLEditor;
